<template>
  <div class="cplan-unit-card-outer">
    <div class="cplan-unit-card" :class="[unitLocked ? 'is-locked' : '', error ? 'is-error' : '']">
      <div :class="['cplan-unit-card__type-label', labelClass]">{{item.firstLevelTitle}}</div>
      <div v-if="error" class="cplan-unit-card__error-icon"><i class="far fa-siren-on"></i></div>

      <div class="cplan-unit-card__top">
        <div class="cplan-unit-card__head">
          <div class="cplan-unit-card__code">{{ unit.code }}</div>
          <div class="cplan-unit-card__credits">{{ unit.creditPoints }} credit points</div>
        </div>
        <div class="cplan-unit-card__title">
          <button type="button" class="btn-link-bold" v-on:click="clickUnitTitle">{{item.relationship.name}}</button>
        </div>
        <div class="cplan-unit-card__offerings" v-if="offerings.length > 0">
          {{offerings.join(', ')}}
        </div>
        <div v-if="error" class="cplan-unit-card__error-text">Unexpected error!</div>
      </div>

      <div class="cplan-unit-card__actions print-hide">
        <MoveUnitPulldown :code="unit.code" :periods="periods" @displayError="(errorVisible, ctx)=> { displayMoveError=errorVisible; moveErrorContext=ctx;}" />
        <div class="cplan-unit-card__action-lock">
          <button type="button" class="btn-icon cplan-unit-card__lock-btn" v-on:click="toggleUnitLock" :id="`tooltip-lock-${unit.code}`">
            <i v-if="!unitLocked" class="far fa-lock-open-alt cplan-unit-card__lock-icon-default"></i>
            <i v-if="!unitLocked" class="far fa-lock-alt cplan-unit-card__lock-icon-hover"></i>
            <i v-if="unitLocked" class="fas fa-lock"></i>
          </button>
        </div>
      </div>

      <transition name="pop">
        <div class="unit-card__alert-bottom prereq" v-if="displayMoveError">
          <span class="unit-card__alert-bottom-content" :id="`tooltip-prereq-alert-${unit.code}`">
            Error moving unit: <span v-if="moveErrorContext">{{moveErrorContext.join(',')}} requisites cannot be met</span><span v-else>{{unit.code}}, prerequsites cannot be met</span>
          </span>
        </div>
      </transition>

    </div>

    <b-tooltip :target="`tooltip-lock-${unit.code}`" triggers="hover" placement="left">
      Locking a unit will keep it in the same teaching period when 'Regenerating' your course plan above.
    </b-tooltip>

  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import SlideOutUnitDetailsView from '../select-units/slide-out-tree-view/SlideOutUnitDetailsView.vue'
import MoveUnitPulldown from './MoveUnitPulldown.vue'
import StudentPreferences from 'client/js/models/student-preferences.js'
import teachingPeriodLabel from 'client/js/utils/teachingPeriodLabel.js'

export default {
  props: {
    unit: { type: Object, required: true },
    year: { type: Number, required: true },
    startYear: { type: Number, required: true },
    teachingPeriod: { type: String, required: true }
  },
  data() {
    return {
      slideOutComponent: SlideOutUnitDetailsView,
      unitLocked: false,
      error: false,
      displayMoveError: false,
      moveErrorContext: null
    }
  },
  computed: {
    ...mapGetters({
      course: 'courses/getSelectedCourse',
      selectedPreferences: 'preferences/getSelectedStudyPreferences',
      selectedItems: 'units/getSelectedCurriculumItems',
      plan: 'plans/getPlanData',
      getLockedUnits: 'plans/getLockedUnits',
      getInput: 'plans/getInput'
    }),
    item() {
      return this.selectedItems.find((u) => u.id === this.unit.id)
    },
    labelClass() {
      return `unit-${this.item.firstLevelTitle.toLowerCase().replace(/\s/gi, '-')}`
    },
    validLocations() {
      if (this.course && this.selectedPreferences) {
        return StudentPreferences.getCourseLocations(this.course, this.selectedPreferences.studentType, this.selectedPreferences.placeOfResidence)
      }
      return []
    },
    offerings() {
      let offerings = []
      if (this.item.relationship && this.item.relationship.unit && this.item.relationship.unit.offerings) {
        let validUnitOfferings = this.item.relationship.unit.offerings.filter(
          (offering) => offering.location && offering.location.id && this.validLocations.includes(offering.location.id)
        )
        validUnitOfferings.forEach((offering) => {
          //if we have never seen the offering before
          if (
            !offerings.includes(offering.teachingScheduled) &&
            // and it's start year is greater than the selected start year
            offering.startYear <= this.startYear &&
            //and it doesnt have an end year, or it's end year is greater than our start year.
            (!offering.endYear || offering.endYear >= this.startYear)
          )
            //add the offering to the list
            offerings.push(offering.teachingScheduled)
        })
      }
      return offerings.sort()
    },
    periods() {
      const units = this.getInput.units
      const code = this.unit.code
      const thisPlanYear = this.year
      const thisplanTeachingPeriod = this.teachingPeriod
      const periods = this.plan.years
        .map((year, yearIndex) =>
          year?.teachingPeriods.map((period) => {
            const loopYear = yearIndex + 1
            const hasOfferings = units
              .filter((x) => x.code == code)
              .filter(
                (x) =>
                  x.defaultOfferings.includes(period.teachingScheduled) ||
                  x.offerings.some((y) => y.year == loopYear && y.teachingPeriods.includes(period.teachingScheduled))
              )
              .some((x) => true)
            if (hasOfferings && !(loopYear == thisPlanYear && period.teachingScheduled == thisplanTeachingPeriod)) {
              return {
                value: loopYear + '_' + period.teachingScheduled, // This is the year and schedule (exactly) that is going to be matched to the avaliable teaching schedules in the course plan builder
                label: this.startYear + parseInt(yearIndex) + ' ' + teachingPeriodLabel(StudentPreferences, period.teachingScheduled)
              }
            }
            return
          })
        )
        .flat()
        .filter((x) => x != null)
      return periods
    }
  },
  methods: {
    ...mapMutations('global', {
      openSlideOut: 'openSlideOut'
    }),
    ...mapActions({
      unitLockSet: 'plans/unitLockSet',
      unitLockClear: 'plans/unitLockClear'
    }),
    toggleUnitLock() {
      if (!this.unitLocked) {
        this.unitLockSet({ code: this.unit.code, year: this.year.toString(), teachingPeriod: this.teachingPeriod })
      } else {
        this.unitLockClear(this.unit.code)
      }
      this.unitLocked = this.isUnitLocked()
    },
    clickUnitTitle() {
      this.item.canComplete = false
      this.openSlideOut({ component: this.slideOutComponent, componentProperties: this.item })
    },
    isUnitLocked() {
      return this.getLockedUnits.some((x) => x.code == this.unit.code)
    }
  },
  mounted() {
    this.unitLocked = this.isUnitLocked()
  },
  components: { MoveUnitPulldown }
}
</script>

